import { FormikProps } from 'formik';
import { Form, Row, Col } from 'antd';
import { MDatePicker, MInput, MSelect, MTextArea } from '@Components/Base';
import { OptionNum } from '@Components/types/options';
import {
  parseDateTime,
  prepareDatePickerValue,
} from '@Utils/transformDateTime';
import dayjs from 'dayjs';

import {
  ResignationRequestFormValues,
  ResignationDateChangeHandler,
} from '../types';

interface RequestDetailsSectionProps {
  formik: FormikProps<ResignationRequestFormValues>;
  label: string;
  formApproval: boolean;
  reasonOptions: OptionNum[];
  onResignationDateChange?: ResignationDateChangeHandler;
}

const RequestDetailsSection: React.FC<RequestDetailsSectionProps> = ({
  formik,
  formApproval,
  reasonOptions,
  onResignationDateChange,
}) => {
  const {
    resignationDate: resignationDateTouched,
    letterOfContent: letterOfContentTouched,
    reason: reasonTouched,
  } = formik.touched;
  const {
    resignationDate: resignationDateError,
    letterOfContent: letterOfContentError,
    reason: reasonError,
  } = formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          label="Resignation Date"
          validateStatus={
            resignationDateTouched && resignationDateError ? 'error' : ''
          }
          help={
            resignationDateTouched && resignationDateError
              ? resignationDateError
              : ''
          }
          required={!formApproval}
        >
          <MDatePicker
            onBlur={() => {
              formik.setFieldTouched('resignationDate', true);
            }}
            className="leave-deatails-datepicker"
            name="resignationDate"
            value={
              formik.values.resignationDate &&
              dayjs(formik.values.resignationDate).tz('UTC')
            }
            format="DD MMM YYYY"
            onChange={(value) => {
              formik.setFieldValue(
                'resignationDate',
                prepareDatePickerValue(value, true)
              );
              onResignationDateChange?.(value);
            }}
            disabled={formApproval}
            disabledDate={(current) => {
              // Disable past days
              return current && current < dayjs().startOf('day');
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Approx Settlement Amount"
          validateStatus={formik.errors.approxSettlementAmount ? 'error' : ''}
          help={formik.errors.approxSettlementAmount || ''}
        >
          <MInput
            name="approxSettlementAmount"
            value={formik.values.approxSettlementAmount}
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Last Working Date"
          validateStatus={formik.errors.lastWorkingDate ? 'error' : ''}
          help={formik.errors.lastWorkingDate || ''}
        >
          <MInput
            name="lastWorkingDate"
            value={parseDateTime(formik.values.lastWorkingDate)}
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Letter Of Content"
          validateStatus={
            letterOfContentTouched && letterOfContentError ? 'error' : ''
          }
          help={
            letterOfContentTouched && letterOfContentError
              ? letterOfContentError
              : ''
          }
          required={!formApproval}
        >
          <MTextArea
            name="letterOfContent"
            value={formik.values.letterOfContent}
            onChange={formik.handleChange}
            onBlur={() => {
              formik.setFieldTouched('letterOfContent', true);
            }}
            rows={2}
            showCount
            disabled={formApproval}
            placeholder="Leave a comment"
            maxLength={5000}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Reason"
          validateStatus={reasonTouched && reasonError ? 'error' : ''}
          help={reasonTouched && reasonError ? reasonError : ''}
          required={!formApproval}
        >
          <MSelect
            value={formik.values.reason}
            options={reasonOptions}
            onChange={(selectedValue) =>
              formik.setFieldValue('reason', selectedValue)
            }
            onBlur={() => {
              formik.setFieldTouched('reason', true);
            }}
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default RequestDetailsSection;
